/*
 * CliC — made-to-order checkout acknowledgment gate (RX_SPEC.md §14 P2;
 * CUSTOM_LENS_SPEC.md §9.1)
 *
 * When the cart holds a made-to-order lens pair added by the Lens Flow wizard — a
 * prescription pair (lines sharing a hidden `_rx_group` property) or a Custom Lenses
 * pair (`_custom_group`, 2026-08-26) — the shopper must acknowledge the made-to-order
 * terms in a popup before checkout proceeds. The popup copy follows the pair kind(s)
 * present: prescription wording for Rx, non-prescription wording for Custom, both
 * blocks when the cart holds both. The gate covers BOTH
 * the standard checkout button AND the dynamic express buttons (Shop Pay / PayPal),
 * which otherwise jump straight to an express checkout and bypass the cart.
 *
 * Approach (Mack 2026-06-25): the buttons stay clickable; the first checkout attempt
 * fires a confirm popup. On acknowledge, the gate lifts for the rest of the page view
 * and the action proceeds.
 *
 * Robustness model (after review): the gate is driven by SERVER-RENDERED state so it's
 * never bypassable through an async timing window —
 *   - `armed` is seeded synchronously from window.CLIC_RX_CART_ACK.hasRxLine /
 *     .hasCustomLine (emitted by theme.liquid from cart.items), then kept fresh via
 *     /cart.js on cart events.
 *   - the express buttons are cross-origin iframes (a parent page can't intercept a
 *     click inside them), so a transparent overlay element is SERVER-RENDERED inside the
 *     express container (cart-drawer.liquid + cart.liquid) and activated purely by the
 *     persistent `body.clic-rx-gated` class + head CSS — present the instant the drawer
 *     template clones in, and never wiped by the drawer's innerHTML re-render.
 *   - the standard checkout button is intercepted at the document level (capture phase),
 *     re-querying at click time so it survives re-renders.
 *
 * Scope: Palo Alto only (the in-wizard Rx flow is Palo Alto-only; Canopy is read-only
 * and not in this repo). Fail-safe: no `_rx_group` / `_custom_group` line (or an
 * unreadable /cart.js) disarms the gate (silent no-op). Copy from
 * window.CLIC_RX_CART_ACK (theme settings: cart_rx_* and cart_custom_*).
 */
(function () {
  "use strict";

  var CFG = window.CLIC_RX_CART_ACK || {};
  // Per pair kind: the hidden group prop, the popup copy (from theme settings via
  // CFG, with built-in fallbacks), and the server-seeded presence flag.
  var KINDS = {
    rx: {
      prop: "_rx_group",
      heading: "Confirm your prescription order",
      ariaLabel: "Prescription order acknowledgment",
      notice: CFG.notice || "",
      ackText: CFG.ackText,
      defaultAck:
        "I understand my order includes made-to-order prescription lenses (longer " +
        "fulfillment, prescription return & exchange policy) and want to proceed to checkout.",
      present: !!CFG.hasRxLine,
    },
    custom: {
      prop: "_custom_group",
      heading: "Confirm your custom lens order",
      ariaLabel: "Custom lens order acknowledgment",
      notice: CFG.customNotice || "",
      ackText: CFG.customAckText,
      defaultAck:
        "I understand my order includes made-to-order custom lenses (longer " +
        "fulfillment, custom-order return & exchange policy) and want to proceed to checkout.",
      present: !!CFG.hasCustomLine,
    },
  };
  var KIND_KEYS = ["rx", "custom"];
  var BOTH_HEADING = "Confirm your made-to-order lenses";
  var BOTH_ARIA = "Made-to-order lens acknowledgment";
  var CHECKOUT_SEL = 'button[name="checkout"], [data-cart-checkout-button] button';
  var OVERLAY_SEL = "[data-clic-rx-overlay]";

  var armed = KINDS.rx.present || KINDS.custom.present; // seeded synchronously from server-rendered cart state
  var acked = false; // shopper acknowledged during this page view
  var modalEl = null;

  // ---------- gate state ----------
  // body.clic-rx-gated activates the server-rendered express overlay (head CSS). The
  // standard-button interceptor reads armed/acked directly.
  function applyGate() {
    if (!document.body) return;
    document.body.classList.toggle("clic-rx-gated", armed && !acked);
  }

  // Refresh KINDS[*].present from the live cart; resolves whether ANY pair is present.
  function cartHasRxLines() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return armed; // unreadable cart -> keep current state
        var items = cart.items || [];
        var found = { rx: false, custom: false };
        for (var i = 0; i < items.length; i++) {
          var p = items[i].properties || {};
          for (var k = 0; k < KIND_KEYS.length; k++) {
            if (p[KINDS[KIND_KEYS[k]].prop]) found[KIND_KEYS[k]] = true;
          }
        }
        KINDS.rx.present = found.rx;
        KINDS.custom.present = found.custom;
        return found.rx || found.custom;
      })
      .catch(function () { return armed; }); // unreadable cart -> keep current state
  }

  // The kinds currently in the cart (server-seeded, then live). Falls back to Rx if
  // the gate is armed but neither flag is set (defensive — should not happen).
  function presentKinds() {
    var out = [];
    for (var k = 0; k < KIND_KEYS.length; k++) {
      if (KINDS[KIND_KEYS[k]].present) out.push(KINDS[KIND_KEYS[k]]);
    }
    return out.length ? out : [KINDS.rx];
  }

  // ---------- confirm modal ----------
  function escapeHtml(str) {
    var d = document.createElement("div");
    d.textContent = str == null ? "" : String(str);
    return d.innerHTML;
  }

  function escClose(e) { if (e.key === "Escape") closeModal(); }

  function closeModal() {
    if (modalEl && modalEl.parentNode) modalEl.parentNode.removeChild(modalEl);
    modalEl = null;
    document.removeEventListener("keydown", escClose);
  }

  function openModal(onConfirm) {
    closeModal();
    var kinds = presentKinds();
    var heading = kinds.length > 1 ? BOTH_HEADING : kinds[0].heading;
    var aria = kinds.length > 1 ? BOTH_ARIA : kinds[0].ariaLabel;
    var body = "";
    for (var i = 0; i < kinds.length; i++) {
      var kind = kinds[i];
      var ackText = (kind.ackText && String(kind.ackText).trim()) || kind.defaultAck;
      body +=
        (kind.notice ? '<div class="clic-rx-ack__notice">' + kind.notice + "</div>" : "") +
        '<p class="clic-rx-ack__ack">' + escapeHtml(ackText) + "</p>";
    }
    var root = document.createElement("div");
    root.className = "clic-rx-ack";
    root.innerHTML =
      '<div class="clic-rx-ack__backdrop" data-ack-cancel></div>' +
      '<div class="clic-rx-ack__dialog" role="dialog" aria-modal="true" aria-label="' + escapeHtml(aria) + '">' +
      '<div class="clic-rx-ack__head">' + escapeHtml(heading) + "</div>" +
      body +
      '<div class="clic-rx-ack__actions">' +
      '<button type="button" class="clic-rx-ack__cancel" data-ack-cancel>Go back</button>' +
      '<button type="button" class="clic-rx-ack__confirm" data-ack-confirm>I understand — continue</button>' +
      "</div></div>";
    document.body.appendChild(root);
    modalEl = root;
    root.addEventListener("click", function (e) {
      if (e.target.closest("[data-ack-confirm]")) { closeModal(); onConfirm(); }
      else if (e.target.closest("[data-ack-cancel]")) { closeModal(); }
    });
    document.addEventListener("keydown", escClose);
    var c = root.querySelector(".clic-rx-ack__confirm");
    if (c) c.focus();
  }

  // ---------- click interception (capture phase, document-level) ----------
  function onDocClick(e) {
    if (!armed || acked) return; // disarmed or already acknowledged -> let it through

    // Express buttons: the click lands on our overlay (over the cross-origin iframe).
    if (e.target.closest(OVERLAY_SEL)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      // After acknowledging, the overlay disappears (gate lifts) and the shopper taps
      // the express button again — we can't synthesize a click into the iframe.
      openModal(function () { acked = true; applyGate(); });
      return;
    }

    // Standard checkout button: re-triggered after acknowledgment (the listener no-ops
    // once acked, so the re-dispatched click submits the form natively).
    var btn = e.target.closest(CHECKOUT_SEL);
    if (btn) {
      e.preventDefault();
      e.stopImmediatePropagation();
      openModal(function () {
        acked = true;
        applyGate();
        try { btn.click(); } catch (err) {
          var f = btn.form || (btn.closest && btn.closest("form"));
          if (f && typeof f.submit === "function") f.submit();
        }
      });
    }
  }

  // ---------- arm/disarm on cart state ----------
  var debounce = null;
  function scheduleSync() {
    if (debounce) clearTimeout(debounce);
    debounce = setTimeout(sync, 120);
  }

  function sync() {
    cartHasRxLines().then(function (has) {
      armed = has;
      applyGate();
    });
  }

  // ---------- wire-up ----------
  applyGate(); // reflect the synchronously-seeded armed state immediately

  // One persistent capture-phase listener handles both the standard button and the
  // express overlay, re-querying at click time so it survives drawer re-renders.
  document.addEventListener("click", onDocClick, true);

  // Keep armed fresh as the cart changes (drawer + page).
  ["theme:cart:update", "theme:cart:added", "theme:cart:refresh", "theme:cart:loaded"].forEach(
    function (ev) { document.addEventListener(ev, scheduleSync); },
  );

  // Belt-and-suspenders: re-sync after cart-mutating fetches we didn't originate.
  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var p = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
        p.then(function () { scheduleSync(); }).catch(function () {});
      }
      return p;
    };
  }

  // Confirm the seeded state against the live cart once on load.
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", sync);
  else sync();
})();
